import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecruiterWorkspaceService } from '../../services/useRecruiterWorkspaceService';
import {
    RecruiterWorkspaceDashboard,
    RecruiterWorkspaceJobDetail,
} from '../../types/recruiterWorkspace';
import './recruiterworkspacepage.css';

type WorkspaceTab = 'overview' | 'reviews' | 'comparisons';

const RecruiterWorkspacePage: FC = () => {
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getDashboard, getJob } = useRecruiterWorkspaceService();
    const [dashboard, setDashboard] = useState<RecruiterWorkspaceDashboard | null>(null);
    const [detail, setDetail] = useState<RecruiterWorkspaceJobDetail | null>(null);
    const [selectedJobId, setSelectedJobId] = useState(searchParams.get('jobPostingId') ?? '');
    const [tab, setTab] = useState<WorkspaceTab>('overview');
    const [loading, setLoading] = useState(true);
    const [loadingJob, setLoadingJob] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getDashboard();
            setDashboard(result);
            setSelectedJobId(current => current || result.jobs[0]?.job_posting_id || '');
        } catch (err: any) {
            setError(err?.response?.data?.message ?? err?.message ?? 'Unable to load the recruiter workspace.');
        } finally {
            setLoading(false);
        }
    }, [getDashboard]);

    useEffect(() => { loadDashboard(); }, [loadDashboard]);

    useEffect(() => {
        if (!selectedJobId) {
            setDetail(null);
            return;
        }
        setSearchParams({ jobPostingId: selectedJobId }, { replace: true });
        setLoadingJob(true);
        getJob(selectedJobId)
            .then(setDetail)
            .catch(() => setError('Unable to load this job workspace.'))
            .finally(() => setLoadingJob(false));
    }, [selectedJobId, getJob, setSearchParams]);

    const selectJob = (jobId: string) => {
        setSelectedJobId(jobId);
        setTab('overview');
    };

    if (loading) return <main className="rw-page"><div className="rw-loading">Building your recruiter workspace…</div></main>;

    return (
        <main className="rw-page">
            <header className="rw-header">
                <div>
                    <div className="rw-eyebrow">Hiring intelligence command center</div>
                    <h1>Recruiter Workspace</h1>
                    <p>Move from saved role to evidence-backed screening and comparison without losing the decision trail.</p>
                </div>
                <div className="rw-header-actions">
                    <button className="rw-btn ghost" onClick={loadDashboard}>Refresh</button>
                    <button className="rw-btn primary" onClick={() => nav('/jobpostings/new')}>+ Add Job</button>
                </div>
            </header>

            {error && <div className="rw-error">{error}</div>}

            <section className="rw-kpis">
                <article><span>Active roles</span><strong>{dashboard?.active_jobs ?? 0}</strong><small>Saved job workspaces</small></article>
                <article><span>Reviewed candidates</span><strong>{dashboard?.reviewed_candidates ?? 0}</strong><small>Distinct resume versions</small></article>
                <article><span>Recruiter reviews</span><strong>{dashboard?.recruiter_reviews ?? 0}</strong><small>Evidence-based screens</small></article>
                <article><span>Comparisons</span><strong>{dashboard?.candidate_comparisons ?? 0}</strong><small>Recorded decisions</small></article>
            </section>

            {!dashboard?.jobs.length ? (
                <section className="rw-empty">
                    <div className="rw-empty-icon">◎</div>
                    <h2>Your first role starts the workspace</h2>
                    <p>Save a job posting, review candidate evidence, then compare the strongest submissions.</p>
                    <button className="rw-btn primary" onClick={() => nav('/jobpostings/new')}>Add Job Posting</button>
                </section>
            ) : (
                <section className="rw-layout">
                    <aside className="rw-job-list">
                        <div className="rw-list-heading"><span>Roles</span><b>{dashboard.jobs.length}</b></div>
                        {dashboard.jobs.map(job => (
                            <button
                                key={job.job_posting_id}
                                className={`rw-job-card ${selectedJobId === job.job_posting_id ? 'active' : ''}`}
                                onClick={() => selectJob(job.job_posting_id)}
                            >
                                <div className="rw-job-title"><strong>{job.title}</strong><span>{job.average_match_score || '—'}</span></div>
                                <div className="rw-company">{job.company || 'Company not specified'}</div>
                                <div className="rw-job-stats">
                                    <span>{job.candidate_count} candidates</span>
                                    <span>{job.recruiter_review_count} reviews</span>
                                    <span>{job.comparison_count} comparisons</span>
                                </div>
                                <small>Updated {new Date(job.last_activity_utc).toLocaleDateString()}</small>
                            </button>
                        ))}
                    </aside>

                    <div className="rw-work-area">
                        {loadingJob || !detail ? <div className="rw-loading">Loading role intelligence…</div> : (
                            <>
                                <section className="rw-role-hero">
                                    <div>
                                        <div className="rw-role-meta">{detail.company || 'Company not specified'}{detail.source ? ` · ${detail.source}` : ''}</div>
                                        <h2>{detail.title}</h2>
                                        <div className="rw-role-pills">
                                            <span>{detail.candidate_count} candidates</span>
                                            <span>{detail.average_match_score || '—'} avg match</span>
                                            <span>{detail.comparison_count} comparisons</span>
                                        </div>
                                    </div>
                                    <div className="rw-role-actions">
                                        <button className="rw-btn primary" onClick={() => nav(`/recruiterreview?jobPostingId=${detail.job_posting_id}`)}>Review Candidate</button>
                                        <button className="rw-btn ghost" onClick={() => nav(`/candidatecomparison?jobPostingId=${detail.job_posting_id}`)} disabled={detail.recruiter_review_count < 2}>Compare</button>
                                    </div>
                                </section>

                                <nav className="rw-tabs" aria-label="Job workspace sections">
                                    <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>Overview</button>
                                    <button className={tab === 'reviews' ? 'active' : ''} onClick={() => setTab('reviews')}>Reviews <span>{detail.reviews.length}</span></button>
                                    <button className={tab === 'comparisons' ? 'active' : ''} onClick={() => setTab('comparisons')}>Comparisons <span>{detail.comparisons.length}</span></button>
                                </nav>

                                {tab === 'overview' && <Overview detail={detail} onTab={setTab} />}
                                {tab === 'reviews' && <ReviewHistory detail={detail} />}
                                {tab === 'comparisons' && <ComparisonHistory detail={detail} />}
                            </>
                        )}
                    </div>
                </section>
            )}
        </main>
    );
};

const Overview: FC<{ detail: RecruiterWorkspaceJobDetail; onTab: (tab: WorkspaceTab) => void }> = ({ detail, onTab }) => (
    <div className="rw-overview-grid">
        <article className="rw-panel rw-description">
            <div className="rw-panel-heading"><span>Role brief</span>{detail.source_url && <a href={detail.source_url} target="_blank" rel="noreferrer">Source ↗</a>}</div>
            <p>{detail.job_description}</p>
        </article>
        <article className="rw-panel rw-funnel">
            <div className="rw-panel-heading"><span>Evidence pipeline</span></div>
            <div className="rw-funnel-row"><b>{detail.initial_analysis_count}</b><span>Initial analyses</span></div>
            <div className="rw-funnel-row"><b>{detail.recruiter_review_count}</b><span>Recruiter reviews</span></div>
            <div className="rw-funnel-row"><b>{detail.comparison_count}</b><span>Candidate comparisons</span></div>
        </article>
        <article className="rw-panel rw-recent">
            <div className="rw-panel-heading"><span>Recent candidate evidence</span><button onClick={() => onTab('reviews')}>View all</button></div>
            {detail.reviews.slice(0, 3).map(review => (
                <div className="rw-mini-review" key={review.recruiter_review_id}>
                    <div><strong>{review.candidate_label}</strong><small>{review.submission_confidence} confidence</small></div>
                    <span>{review.candidate_match_score}</span>
                </div>
            ))}
            {!detail.reviews.length && <p className="rw-muted">No candidates reviewed for this role yet.</p>}
        </article>
    </div>
);

const ReviewHistory: FC<{ detail: RecruiterWorkspaceJobDetail }> = ({ detail }) => (
    <section className="rw-history">
        {!detail.reviews.length && <div className="rw-history-empty">No Recruiter Reviews recorded for this role.</div>}
        {detail.reviews.map(review => (
            <details className="rw-history-card" key={review.recruiter_review_id}>
                <summary>
                    <div className="rw-history-score">{review.candidate_match_score}</div>
                    <div><strong>{review.candidate_label}</strong><span>{review.submission_confidence} submission confidence · {new Date(review.created_utc).toLocaleString()}</span></div>
                    <div className="rw-history-counts"><span>{review.verification_needed_count} verify</span><span>{review.verification_question_count} questions</span></div>
                </summary>
                <div className="rw-history-body"><p>{review.overall_summary}</p><div><b>Next step</b>{review.recommended_next_step}</div></div>
            </details>
        ))}
    </section>
);

const ComparisonHistory: FC<{ detail: RecruiterWorkspaceJobDetail }> = ({ detail }) => (
    <section className="rw-history">
        {!detail.comparisons.length && <div className="rw-history-empty">No Candidate Comparisons recorded for this role.</div>}
        {detail.comparisons.map(comparison => (
            <details className="rw-history-card comparison" key={comparison.comparison_record_id}>
                <summary>
                    <div className="rw-compare-icon">⇄</div>
                    <div><strong>{comparison.candidate_count}-candidate comparison</strong><span>{new Date(comparison.created_utc).toLocaleString()}</span></div>
                    <div className="rw-rank-preview">{comparison.rankings.slice(0, 3).map(rank => <span key={rank.recruiter_review_id}>#{rank.rank} {rank.candidate_label}</span>)}</div>
                </summary>
                <div className="rw-history-body">
                    <p>{comparison.overall_summary}</p>
                    <div className="rw-ranking-list">{comparison.rankings.map(rank => (
                        <article key={rank.recruiter_review_id}>
                            <div className="rw-rank">#{rank.rank}</div>
                            <div><strong>{rank.candidate_label}</strong><p>{rank.ranking_explanation}</p><small>Evidence {rank.evidence_confidence} · Interview risk {rank.interview_risk}</small></div>
                            <b>{rank.overall_match_score}</b>
                        </article>
                    ))}</div>
                </div>
            </details>
        ))}
    </section>
);

export default RecruiterWorkspacePage;
